import { MarkerClusterer } from '@googlemaps/markerclusterer'
import { useRef } from 'react'

import { useIsomorphicLayoutEffect } from '../../../../core/hook/useIsomorphicLayout'
import { CustomCmsSeller } from '../../../../domain/seller'
import { setInfoWindowContent } from '../helper'

import { CustomGoogleMapMarker, SellersGoogleMapOptions } from './types'
import { googleGetMapEl } from './helpers'

const generateMarkersGoogleMap = (
  map: google.maps.Map,
  sellers: CustomCmsSeller[],
  options: SellersGoogleMapOptions,
) => {
  const infoWindow = new google.maps.InfoWindow({
    content: '',
    maxWidth: 300,
  })

  const markers = sellers.map((seller) => {
    const marker = new google.maps.Marker({
      position: {
        lat: Number(seller.latitude),
        lng: Number(seller.longitude),
      },
      map,
      icon: `/assets/img/map/marker/${seller.isMessika ? 'marker_01.png' : 'marker_02.png'}`,
    }) as CustomGoogleMapMarker
    marker.id = seller.id

    marker.addListener('click', () => {
      infoWindow.setContent(setInfoWindowContent(seller))
      infoWindow.open(map, marker)
      options.onClickMarker?.(seller)
    })

    return marker
  })

  const markerClusterer = new MarkerClusterer({
    markers,
    map,
    renderer: {
      render: ({ count, position }) =>
        new google.maps.Marker({
          position,
          label: {
            text: count.toString(),
            color: '#fff',
          },
          icon: '/assets/img/map/markerclusterer/m1.png',
        }),
    },
  })

  return { markerClusterer, markers }
}

const useSellersGoogleMap = (sellers: CustomCmsSeller[], options: SellersGoogleMapOptions = {}) => {
  const mapRef = useRef<google.maps.Map>()
  const markersRef = useRef<CustomGoogleMapMarker[]>()
  const markerClustererRef = useRef<MarkerClusterer>()

  useIsomorphicLayoutEffect(() => {
    const mapSellersEl = googleGetMapEl()
    if (!mapSellersEl) {
      return () => {}
    }

    // generate google map
    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(mapSellersEl, {
        center: { lat: 33.990374, lng: -6.849423 },
        zoom: 2,
        minZoom: 2,
      })
    }

    mapRef.current.addListener('zoom_changed', () => {
      if (options.onZoomChange) {
        options.onZoomChange(mapRef.current as google.maps.Map)
      }
    })

    // generate markers and clusters
    const { markerClusterer, markers } = generateMarkersGoogleMap(mapRef.current, sellers, options)
    markerClustererRef.current = markerClusterer
    markersRef.current = markers

    return () => markerClustererRef.current?.setMap(null)
  }, [sellers])

  return { mapRef, markerClustererRef, markersRef }
}

export default useSellersGoogleMap
